import qs from 'qs'
import { QueryFunctionContext } from '@tanstack/react-query'

import {
	InsertItemResponse,
	DeleteItemResponse,
	UpdateItemResponse,
	SelectItemResponse,
} from '@src/interface/api/response.interface'
import client from '@src/lib/api/client'
import {
	GetCompanyChallengesResBody,
	GetCompanyChallengesQueryKey,
	DraftCompanyChallengeDto,
	CreateCompanyChallengeDto,
	ModifyCompanyChallengeDto,
	CreateHotdealChallengeDto,
	DraftHotdealChallengeDto,
	UpdateCompanyChallengeStatusDto,
	CompanyChallengeCategoryDto,
} from '@src/type/challenge'

export async function createHotdealChallenge(createDto: CreateHotdealChallengeDto) {
	const response = await client.post<InsertItemResponse<Record<string, any>>>(
		'challenges',
		createDto,
	)

	if (response.data.success !== 1) {
		throw new Error('fail to create hotdeal challenge')
	}
}

export async function draftHotdealChallenge(draftDto: DraftHotdealChallengeDto) {
	const response = await client.post<InsertItemResponse<Record<string, any>>>(
		'challenges/company-challenge-data/temporary',
		draftDto,
	)

	if (response.data.success !== 1) {
		throw new Error('fail to create draft challenge')
	}
}

export async function getCompanyChallenges({
	queryKey: [_key, vendorNo, currentPage],
}: QueryFunctionContext<GetCompanyChallengesQueryKey>): Promise<GetCompanyChallengesResBody> {
	if (vendorNo === undefined) {
		return {
			data: {
				items: [],
				meta: {
					current_page: 0,
					has_next_page: false,
					total_count: 0,
					total_page: 0,
				},
			},
			success: 1,
		}
	}

	const queryString = qs.stringify({
		page: currentPage,
		take: 10,
	})

	const response = await client.get<GetCompanyChallengesResBody>(
		`/challenges/company-challenge-data/company/${vendorNo}?${queryString}`,
	)

	if (response.data.success !== 1) {
		throw new Error('fail to get company challenges')
	}

	return response.data
}

export async function copyCompanyChallenge(companyChallengeNo: number) {
	const response = await client.post<InsertItemResponse<Record<string, any>>>(
		`/challenges/company-challenge-data/${companyChallengeNo}/copy`,
	)

	if (response.data.success !== 1) {
		throw new Error('fail to copy hotdeal challenge')
	}
}

export async function draftCompanyChallenge({
	company_challenge_no,
	...draftDto
}: DraftCompanyChallengeDto) {
	const response = await client.patch<UpdateItemResponse<Record<string, any>>>(
		`/challenges/company-challenge-data/${company_challenge_no}`,
		draftDto,
	)

	if (response.data.success !== 1) {
		throw new Error('fail to draft hotdeal challenge')
	}
}

export async function createCompanyChallenge(createDto: CreateCompanyChallengeDto) {
	const response = await client.post<InsertItemResponse<Record<string, any>>>(
		`/challenges/company-challenge-data/${createDto.company_challenge_no}/temporary`,
		createDto,
	)

	if (response.data.success !== 1) {
		throw new Error('fail to create hotdeal challenge')
	}
}

export async function modifyCompanyChallenge({
	company_challenge_no,
	...modifyDto
}: ModifyCompanyChallengeDto) {
	const response = await client.patch<UpdateItemResponse<Record<string, any>>>(
		`/challenges/company-challenge-data/${company_challenge_no}`,
		modifyDto,
	)

	if (response.data.success !== 1) {
		throw new Error('fail to draft hotdeal challenge')
	}
}

export async function deleteCompanyChallenge(companyChallengeNo: number) {
	const response = await client.delete<DeleteItemResponse<Record<string, any>>>(
		`/challenges/company-challenge-data/${companyChallengeNo}`,
	)

	if (response.data.success !== 1) {
		throw new Error('fail to delete hotdeal challenge')
	}
}

export async function updateCompanyChallengeStatus({
	companyChallengeNo,
	status,
}: UpdateCompanyChallengeStatusDto) {
	const response = await client.patch<UpdateItemResponse<Record<string, any>>>(
		`/challenges/company-challenge-data/${companyChallengeNo}/status`,
		{
			status,
		},
	)

	if (response.data.success !== 1) {
		throw new Error('fail to update hotdeal challenge status')
	}

	return response.data
}

export async function getCompanyChallengeCategories(): Promise<CompanyChallengeCategoryDto[]> {
	const response = await client.get<
		SelectItemResponse<{ company_challenge_categories: CompanyChallengeCategoryDto[] }>
	>('/challenges/company-challenge-category')

	if (response.data.success !== 1) {
		throw new Error('fail to get company challenge categories')
	}

	return response.data.data.company_challenge_categories
}
